import { Group, Text } from '@mantine/core';
import type { ReactNode } from 'react';
import { useTheme } from '@/assets/styles/themeMantine';
import { HelpPopover } from '@/components/HelpPopover/HelpPopover';

interface Help {
  link?: string;
  linkText?: string;
  text?: string;
  onOpen?: () => void;
}

interface SectionHeaderProps {
  title: string;
  /** Optional help-popover icon shown next to the title. */
  help?: Help;
  /** Optional content pushed to the right edge (e.g. a rating badge). */
  rightSlot?: ReactNode;
}

/**
 * Shared section heading used across the wildfire group Summary tab —
 * an h7-styled, secondary-foreground label with either an inline
 * HelpPopover icon or a right-aligned content slot. Centralises the
 * typography choice so the three sections stay visually aligned.
 */
export const SectionHeader = ({
  title,
  help,
  rightSlot,
}: SectionHeaderProps) => {
  const theme = useTheme();
  const titleNode = (
    <Text
      style={theme.other.typography.variants.h7}
      c={theme.other.mainColor.foreground.secondary}
    >
      {title}
    </Text>
  );

  if (rightSlot !== undefined) {
    return (
      <Group justify="space-between" align="center">
        {titleNode}
        {rightSlot}
      </Group>
    );
  }

  return (
    <Group gap={6} align="center">
      {titleNode}
      {help && (
        <HelpPopover
          link={help.link}
          linkText={help.linkText}
          text={help.text}
          onOpen={help.onOpen}
          iconName="HelpOutline"
        />
      )}
    </Group>
  );
};
